import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core';
import { useShareablesTemplateRoot } from '../../hooks/useShareablesTemplateRoot';
import { useImagesLoaded } from '../../hooks/useImagesLoaded';
import Chart from './Chart';
import skins, {
    COLOR_DIVIDER,
    COLOR_INK,
    COLOR_SOURCE,
    COLOR_TEAL,
    DEFAULT_SKIN,
    EQUAL_HOUSING_LOGO_PATH,
    LOGO_PATH,
} from './skins';
import {
    CHART_TYPE_PRICE_VS_INVENTORY,
    getPriceVsInventoryCaption,
    getSingleMetricCaption,
} from './captions';

const HEADER_TOP = 36;
const HEADER_LEFT = 42;
const HEADER_WIDTH = 816;
const FOOTER_TOP = 560;
const COPYRIGHT_YEAR = new Date().getFullYear();

const useStyles = makeStyles({
    wrap: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        fontFamily: "'LL Circular Pro', Helvetica, Arial, sans-serif",
        color: COLOR_INK,
        overflow: 'hidden',
    },
    band: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
    },
    illustration: {
        position: 'absolute',
    },
    header: {
        position: 'absolute',
        top: HEADER_TOP,
        left: HEADER_LEFT,
    },
    area: {
        fontSize: 24,
        lineHeight: '32px',
        letterSpacing: 1,
    },
    stat: {
        color: COLOR_TEAL,
        fontWeight: 700,
        letterSpacing: 1,
    },
    headline: {
        fontWeight: 700,
        letterSpacing: 1,
    },
    subline: {
        fontWeight: 400,
        letterSpacing: 1,
    },
    chart: {
        position: 'absolute',
    },
    legend: {
        position: 'absolute',
        left: 0,
        right: 0,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 16,
        fontSize: 16,
        fontWeight: 700,
        letterSpacing: 1,
    },
    legendItem: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
    },
    legendSolid: {
        width: 28,
        borderTop: `3px solid ${COLOR_INK}`,
    },
    legendDashed: {
        width: 28,
        borderTop: `3px dotted ${COLOR_INK}`,
    },
    divider: {
        position: 'absolute',
        left: 0,
        right: 0,
        borderTop: `1px solid ${COLOR_DIVIDER}`,
    },
    footer: {
        position: 'absolute',
        top: FOOTER_TOP,
        left: 24,
        right: 24,
        height: 24,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    logo: {
        display: 'block',
        flexShrink: 0,
        width: '93px',
    },
    disclaimer: {
        display: 'flex',
        alignItems: 'center',
        flexShrink: 0,
        gap: 8,
        fontSize: 10,
        lineHeight: '12px',
        color: COLOR_SOURCE,
        whiteSpace: 'nowrap',
    },
    equalHousingLogo: {
        display: 'block',
        flexShrink: 0,
        width: '24px',
    },
    watermark: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
});

function skinTextStyle(config) {
    if (!config) return undefined;
    return { fontSize: config.fontSize, lineHeight: `${config.lineHeight}px` };
}

function useFontsReady() {
    const [ready, setReady] = useState(!(document.fonts && document.fonts.ready));

    useEffect(()=> {
        if (ready) return;
        let cancelled = false;
        document.fonts.ready.then(()=> {
            if (!cancelled) setReady(true);
        });
        return ()=> { cancelled = true; };
    }, [ready]);

    return ready;
}

function Illustration({ config, className, zIndex }) {
    if (!config) return null;

    const { src, width, height, top, left, right } = config;

    return (
        <img
            className={className}
            src={src}
            alt=""
            style={{ width, height, top, left, right, zIndex }}
        />
    );
}

function Legend({ classes, top, labels }) {
    return (
        <div className={classes.legend} style={{ top }}>
            <span className={classes.legendItem}>
                <span className={classes.legendSolid} />
                {labels.left}
            </span>
            <span className={classes.legendItem}>
                <span className={classes.legendDashed} />
                {labels.right}
            </span>
        </div>
    );
}

function AccentedHeadline({ segments }) {
    return (
        <>
            {segments.map(({ text, accent }, index)=> (
                <span key={index} style={accent ? { color: COLOR_TEAL } : undefined}>{text}</span>
            ))}
        </>
    );
}

export default function HousingSnapshotShareable(props) {
    const { rootClasses, parsedData, onImagesLoaded } = useShareablesTemplateRoot(props);
    const classes = useStyles();
    const [chartRendered, setChartRendered] = useState(false);

    const {
        chartType = DEFAULT_SKIN,
        areaLabel,
        series = [],
        primary,
        secondary,
        watermark = false,
    } = parsedData || {};

    const skin = skins[chartType] || skins[DEFAULT_SKIN];
    const illustration = skin.ILLUSTRATION;
    const imagesLoaded = useImagesLoaded([
        LOGO_PATH,
        EQUAL_HOUSING_LOGO_PATH,
        illustration && illustration.src,
    ]);
    const fontsReady = useFontsReady();
    const readyToRender = imagesLoaded && fontsReady;

    const hasSeries = Array.isArray(series) && series.length > 0;
    const renderComplete = hasSeries ? chartRendered : readyToRender;

    useEffect(()=> {
        if (!renderComplete || window.isLoaded) return;
        if (typeof onImagesLoaded === 'function') {
            onImagesLoaded();
        } else {
            window.isLoaded = true;
        }
    }, [renderComplete, onImagesLoaded]);

    if (!parsedData) return null;

    const isDual = chartType === CHART_TYPE_PRICE_VS_INVENTORY;
    const caption = isDual
        ? getPriceVsInventoryCaption(primary, secondary)
        : getSingleMetricCaption(chartType, primary);

    return (
        <>
            <link rel="stylesheet" href="./css/ll-circular-pro.css" />

            <div className={`${rootClasses.root}`} id="templateRoot">
                <div className={rootClasses.viewport}>
                    <div
                        className={classes.wrap}
                        style={{
                            backgroundColor: skin.BACKGROUND_COLOR,
                            backgroundImage: skin.BACKGROUND_IMAGE,
                            textAlign: skin.TEXT_ALIGN,
                        }}
                    >
                        {skin.HEADER_BAND && (
                            <div
                                className={classes.band}
                                style={{
                                    height: skin.HEADER_BAND.height,
                                    backgroundColor: skin.HEADER_BAND.color,
                                }}
                            />
                        )}

                        {illustration && illustration.behindChart && (
                            <Illustration config={illustration} className={classes.illustration} zIndex={1} />
                        )}

                        <div
                            className={classes.chart}
                            style={{ ...skin.CHART, zIndex: 2 }}
                        >
                            {readyToRender && hasSeries && (
                                <Chart
                                    series={series}
                                    chartType={chartType}
                                    axisTitles={skin.AXIS_TITLES}
                                    pointHoleColor={skin.BACKGROUND_COLOR}
                                    onRendered={()=> setChartRendered(true)}
                                />
                            )}
                        </div>

                        {illustration && !illustration.behindChart && (
                            <Illustration config={illustration} className={classes.illustration} zIndex={3} />
                        )}

                        <div
                            className={classes.header}
                            style={{ width: skin.HEADER_WIDTH || HEADER_WIDTH, zIndex: 4 }}
                        >
                            <div className={classes.area}>{areaLabel}</div>

                            {caption && !isDual && (
                                <>
                                    <div className={classes.stat} style={skinTextStyle(skin.STAT)}>
                                        {caption.stat}
                                    </div>
                                    <div className={classes.headline} style={skinTextStyle(skin.HEADLINE)}>
                                        {caption.headline}
                                    </div>
                                    <div className={classes.subline} style={skinTextStyle(skin.SUBLINE)}>
                                        {caption.subline}
                                    </div>
                                </>
                            )}

                            {caption && isDual && (
                                <>
                                    <div className={classes.headline} style={skinTextStyle(skin.HEADLINE)}>
                                        <AccentedHeadline segments={caption.headline} />
                                    </div>
                                    {caption.subline && (
                                        <div className={classes.subline} style={skinTextStyle(skin.SUBLINE)}>
                                            {caption.subline}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        {skin.LEGEND_TOP && skin.AXIS_TITLES && (
                            <Legend classes={classes} top={skin.LEGEND_TOP} labels={skin.AXIS_TITLES} />
                        )}

                        {skin.DIVIDER_TOP && (
                            <div className={classes.divider} style={{ top: skin.DIVIDER_TOP }} />
                        )}

                        <div className={classes.footer}>
                            <img className={classes.logo} src={LOGO_PATH} alt="ListReports" />

                            <span className={classes.disclaimer}>
                                <span>&copy;ListReports, {COPYRIGHT_YEAR}, all rights reserved</span>
                                <span>&bull;</span>
                                <span>
                                    This information is from third-party sources
                                    and its accuracy cannot be guaranteed.
                                </span>
                            </span>

                            <img
                                className={classes.equalHousingLogo}
                                src={EQUAL_HOUSING_LOGO_PATH}
                                alt="Equal Housing Opportunity"
                            />
                        </div>

                        {watermark && (
                            <div className={classes.watermark}>
                                <img src="./images/watermark-stamp.svg" alt="Watermark" />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}
